import { useMemo, useState, useCallback, useEffect, Fragment } from 'react'
import { usePickHistory, useBonusHistory } from '../hooks/usePicks'
import { useParlayHistory } from '../hooks/useParlays'
import { usePropPickHistory, useMyPropLiveStats } from '../hooks/useProps'
import { useFuturesPickHistory } from '../hooks/useFutures'
import { usePickReactionsBatch } from '../hooks/useSocial'
import { useAuth } from '../hooks/useAuth'
import { useAuthStore } from '../stores/authStore'
import GameCard from '../components/picks/GameCard'
import ParlayCard from '../components/picks/ParlayCard'
import PropCard from '../components/picks/PropCard'
import PropLadderCard from '../components/picks/PropLadderCard'
import FuturesPickCard from '../components/picks/FuturesPickCard'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import EmptyState from '../components/ui/EmptyState'
import GameCenterModal from '../components/picks/GameCenterModal'
import MyProfileBanner from '../components/hub/MyProfileBanner'
import UserProfileModal from '../components/profile/UserProfileModal'

function getParlayGameDate(parlay) {
  const starts = (parlay.parlay_legs || [])
    .map((l) => l.games?.starts_at)
    .filter(Boolean)
    .sort()
  return starts[0] || parlay.created_at
}

function parlayHasGameOnDate(parlay, dateKey) {
  return (parlay.parlay_legs || []).some((l) => {
    const start = l.games?.starts_at
    return start && getLocalDateKey(start) === dateKey
  })
}

function parlaySettledOnDate(parlay, dateKey) {
  // A settled parlay belongs to "today" if it was settled today (updated_at)
  return parlay.status === 'settled' && parlay.updated_at && getLocalDateKey(parlay.updated_at) === dateKey
}

function getLocalDateKey(dateStr) {
  const d = new Date(dateStr)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function formatDateHeader(dateKey) {
  const [y, m, d] = dateKey.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function groupByDate(items, getDate) {
  const groups = []
  let currentKey = null
  let currentItems = []
  for (const item of items) {
    const key = getLocalDateKey(getDate(item))
    if (key !== currentKey) {
      if (currentItems.length) groups.push({ date: currentKey, items: currentItems })
      currentKey = key
      currentItems = [item]
    } else {
      currentItems.push(item)
    }
  }
  if (currentItems.length) groups.push({ date: currentKey, items: currentItems })
  return groups
}

function getSessionKey() {
  const d = new Date()
  return `results-collapsed-${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function useTodayKey() {
  const [key, setKey] = useState(() => getLocalDateKey(new Date().toISOString()))
  useEffect(() => {
    const now = new Date()
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
    const ms = tomorrow - now + 100
    const timer = setTimeout(() => setKey(getLocalDateKey(new Date().toISOString())), ms)
    return () => clearTimeout(timer)
  }, [key])
  return key
}

function loadCollapsed() {
  try {
    const key = getSessionKey()
    const stored = sessionStorage.getItem(key)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}


// Several bets on the SAME prop belong on one card, as a ladder. Rendered
// separately they repeat the player's face, name and market once per bet to
// communicate one differing number each — and the thing a ladder actually
// says, that he threw for 225 so the rungs below it cleared, is left for the
// reader to assemble from five separate outcomes.
//
// A single bet stays on the ordinary card: there is no ladder in one rung.
function groupPropsByProp(picks) {
  const groups = new Map()
  for (const p of picks || []) {
    const key = p.prop_id
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(p)
  }
  return [...groups.values()]
}

function renderPropPicks(picks) {
  return groupPropsByProp(picks).map((group) =>
    group.length > 1
      ? <PropLadderCard key={group[0].prop_id} picks={group} />
      : <PropCard key={group[0].id} prop={group[0].player_props} pick={group[0]} />,
  )
}

export default function ResultsPage() {
  const { profile } = useAuth()
  const fetchProfile = useAuthStore((s) => s.fetchProfile)
  const [collapsed, setCollapsed] = useState(loadCollapsed)
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [gameCenterGameId, setGameCenterGameId] = useState(null)
  const session = useAuthStore((s) => s.session)

  // The auth-store profile is fetched once at login and never refreshed,
  // so total_points goes stale after picks settle. Refetch on mount and on
  // window focus so the Net Points summary stays in sync with the server.
  useEffect(() => {
    fetchProfile?.()
    const onFocus = () => { fetchProfile?.() }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [fetchProfile])

  const toggleSection = useCallback((section, defaultCollapsed) => {
    setCollapsed((prev) => {
      const current = prev[section] !== undefined ? prev[section] : !!defaultCollapsed
      const next = { ...prev, [section]: !current }
      sessionStorage.setItem(getSessionKey(), JSON.stringify(next))
      return next
    })
  }, [])

  const { data: picks, isLoading } = usePickHistory()
  const { data: parlays, isLoading: parlaysLoading } = useParlayHistory()
  const { data: propPicks, isLoading: propsLoading } = usePropPickHistory()
  const { data: futuresPicks, isLoading: futuresLoading } = useFuturesPickHistory()
  const { data: bonuses } = useBonusHistory()

  // Separate poll for live prop stats — only runs when there's at least one
  // locked pick on a live game. Merged into propPicks at render time so
  // PropCard's `pick.live_stat` access continues to work unchanged.
  const hasLockedProps = (propPicks || []).some((p) => p.status === 'locked')
  const { data: liveStatsMap } = useMyPropLiveStats({ hasLive: hasLockedProps })

  const todayKey = useTodayKey()

  const { todayPicks, todayParlays, todayProps, liveFutures, olderSettledPicks, olderSettledParlays, olderSettledProps } = useMemo(() => {
    const allPicks = (picks || []).filter(p => p.status === 'locked' || p.status === 'settled')
    const allParlays = (parlays || []).filter(p => p.status === 'locked' || p.status === 'settled')
    // Merge live stats into locked prop picks here so downstream code keeps
    // reading `pick.live_stat` without changes.
    const allProps = (propPicks || [])
      .filter(p => p.status === 'locked' || p.status === 'settled')
      .map(p => liveStatsMap?.[p.id] != null ? { ...p, live_stat: liveStatsMap[p.id] } : p)

    return {
      todayPicks: allPicks.filter(p => getLocalDateKey(p.games?.starts_at) === todayKey),
      todayParlays: allParlays.filter(p => parlayHasGameOnDate(p, todayKey) && (p.status === 'locked' || parlaySettledOnDate(p, todayKey))),
      todayProps: allProps.filter(p => getLocalDateKey(p.player_props?.games?.starts_at || p.created_at) === todayKey),
      liveFutures: (futuresPicks || []).filter(p => p.status === 'locked' && p.futures_markets?.status === 'closed'),
      olderSettledPicks: allPicks.filter(p => p.status === 'settled' && getLocalDateKey(p.games?.starts_at) !== todayKey),
      olderSettledParlays: allParlays.filter(p => p.status === 'settled' && !parlayHasGameOnDate(p, todayKey)),
      olderSettledProps: allProps.filter(p => p.status === 'settled' && getLocalDateKey(p.player_props?.games?.starts_at || p.created_at) !== todayKey),
    }
  }, [picks, parlays, propPicks, futuresPicks, todayKey, liveStatsMap])

  const settledFutures = useMemo(() => (futuresPicks || []).filter(p => p.status === 'settled'), [futuresPicks])

  const hasTodayAction = todayPicks.length > 0 || todayParlays.length > 0 || todayProps.length > 0 || liveFutures.length > 0
  const hasSettled = olderSettledPicks.length > 0 || olderSettledParlays.length > 0 || olderSettledProps.length > 0 || settledFutures.length > 0

  const allSettledPicks = useMemo(() => [...todayPicks.filter(p => p.status === 'settled'), ...olderSettledPicks], [todayPicks, olderSettledPicks])

  const settledPickIds = useMemo(() => {
    return allSettledPicks.map((p) => p.id)
  }, [allSettledPicks])

  const { data: reactionsBatch } = usePickReactionsBatch(settledPickIds)

  if (isLoading || parlaysLoading || propsLoading || futuresLoading) return <LoadingSpinner />

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <h1 className="font-display text-3xl mb-6">Results</h1>

      {profile && (
        <MyProfileBanner
          profile={profile}
          onTap={() => setSelectedUserId(session?.user?.id)}
        />
      )}

      {hasTodayAction && (
        <>
          <button onClick={() => toggleSection('today')} className="flex items-center justify-between w-full mb-3">
            <h2 className="font-display text-lg text-accent">Today's Action</h2>
            <svg className={`w-5 h-5 text-text-muted transition-transform ${collapsed.today ? '' : 'rotate-180'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
          </button>
          {!collapsed.today && (
            <div className="space-y-3 mb-6">
              {todayPicks.map((pick) => (
                <GameCard
                  key={pick.id}
                  game={pick.games}
                  userPick={pick}
                  reactions={reactionsBatch?.[pick.id]}
                  onCardClick={() => setGameCenterGameId(pick.games?.id)}
                  sportKey={pick.games?.sports?.key}
                />
              ))}
              {todayParlays.map((parlay) => (
                <ParlayCard key={parlay.id} parlay={parlay} />
              ))}
              {renderPropPicks(todayProps)}
              {liveFutures.map((fp) => (
                <FuturesPickCard key={fp.id} pick={fp} />
              ))}
            </div>
          )}
        </>
      )}

      {!hasTodayAction && !hasSettled ? (
        <EmptyState title="No results yet" message="Your settled picks will appear here" />
      ) : hasSettled && (
        <>
          {olderSettledPicks.length > 0 && (() => {
            const isCollapsed = collapsed.picks !== undefined ? collapsed.picks : hasTodayAction
            return (
              <>
                <button onClick={() => toggleSection('picks', hasTodayAction)} className="flex items-center justify-between w-full mb-3">
                  <h2 className="font-display text-lg text-text-secondary">Straight Picks</h2>
                  <svg className={`w-5 h-5 text-text-muted transition-transform ${isCollapsed ? '' : 'rotate-180'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                </button>
                {!isCollapsed && (
                  <div className="space-y-3 mb-6">
                    {groupByDate(olderSettledPicks, (p) => p.games.starts_at).map(({ date, items }) => (
                      <Fragment key={date}>
                        <p className="text-xs text-text-muted pt-1">{formatDateHeader(date)}</p>
                        {items.map((pick) => (
                          <GameCard
                            key={pick.id}
                            game={pick.games}
                            userPick={pick}
                            reactions={reactionsBatch?.[pick.id]}
                            onCardClick={() => setGameCenterGameId(pick.games?.id)}
                            sportKey={pick.games?.sports?.key}
                          />
                        ))}
                      </Fragment>
                    ))}
                  </div>
                )}
              </>
            )
          })()}

          {olderSettledParlays.length > 0 && (() => {
            const isCollapsed = collapsed.parlays !== undefined ? collapsed.parlays : hasTodayAction
            return (
              <>
                <button onClick={() => toggleSection('parlays', hasTodayAction)} className="flex items-center justify-between w-full mb-3">
                  <h2 className="font-display text-lg text-text-secondary">Parlays</h2>
                  <svg className={`w-5 h-5 text-text-muted transition-transform ${isCollapsed ? '' : 'rotate-180'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                </button>
                {!isCollapsed && (
                  <div className="space-y-3 mb-6">
                    {groupByDate(olderSettledParlays, (p) => p.created_at).map(({ date, items }) => (
                      <Fragment key={date}>
                        <p className="text-xs text-text-muted pt-1">{formatDateHeader(date)}</p>
                        {items.map((parlay) => (
                          <ParlayCard key={parlay.id} parlay={parlay} />
                        ))}
                      </Fragment>
                    ))}
                  </div>
                )}
              </>
            )
          })()}

          {olderSettledProps.length > 0 && (() => {
            const isCollapsed = collapsed.props !== undefined ? collapsed.props : hasTodayAction
            return (
              <>
                <button onClick={() => toggleSection('props', hasTodayAction)} className="flex items-center justify-between w-full mb-3">
                  <h2 className="font-display text-lg text-text-secondary">Player Props</h2>
                  <svg className={`w-5 h-5 text-text-muted transition-transform ${isCollapsed ? '' : 'rotate-180'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                </button>
                {!isCollapsed && (
                  <div className="space-y-3">
                    {groupByDate(olderSettledProps, (p) => p.player_props?.games?.starts_at || p.created_at).map(({ date, items }) => (
                      <Fragment key={date}>
                        <p className="text-xs text-text-muted pt-1">{formatDateHeader(date)}</p>
                        {renderPropPicks(items)}
                      </Fragment>
                    ))}
                  </div>
                )}
              </>
            )
          })()}

          {/* Leagues */}
          {(() => {
            const isCollapsed = collapsed.leagues !== undefined ? collapsed.leagues : hasTodayAction
            return (
              <>
                <button onClick={() => toggleSection('leagues', hasTodayAction)} className="flex items-center justify-between w-full mb-3">
                  <h2 className="font-display text-lg text-text-secondary">Leagues</h2>
                  <svg className={`w-5 h-5 text-text-muted transition-transform ${isCollapsed ? '' : 'rotate-180'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                </button>
                {!isCollapsed && (
                  bonuses?.length > 0 ? (
                    <div className="space-y-2 mb-6">
                      {bonuses.map((bonus) => (
                        <div key={bonus.id} className="bg-bg-primary/50 backdrop-blur-sm rounded-xl border border-text-primary/20 px-4 py-3 flex items-center justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-text-primary break-words">{bonus.label || bonus.type}</div>
                            <div className="text-xs text-text-muted">{new Date(bonus.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                          </div>
                          <span className={`font-display text-base shrink-0 ${bonus.points > 0 ? 'text-correct' : bonus.points < 0 ? 'text-incorrect' : 'text-text-muted'}`}>
                            {bonus.points > 0 ? '+' : ''}{bonus.points}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 mb-6">
                      <p className="text-sm text-text-muted mb-2">No league points yet</p>
                      <a href="/leagues/join" className="text-sm text-accent hover:text-accent-hover font-semibold transition-colors">
                        Earn points in leagues
                      </a>
                    </div>
                  )
                )}
              </>
            )
          })()}

          {settledFutures.length > 0 && (() => {
            const isCollapsed = collapsed.futures !== undefined ? collapsed.futures : hasTodayAction
            return (
              <>
                <button onClick={() => toggleSection('futures', hasTodayAction)} className="flex items-center justify-between w-full mb-3">
                  <h2 className="font-display text-lg text-text-secondary">Futures</h2>
                  <svg className={`w-5 h-5 text-text-muted transition-transform ${isCollapsed ? '' : 'rotate-180'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                </button>
                {!isCollapsed && (
                  <div className="space-y-3 mb-6">
                    {settledFutures.map((fp) => (
                      <FuturesPickCard key={fp.id} pick={fp} />
                    ))}
                  </div>
                )}
              </>
            )
          })()}
        </>
      )}
      <GameCenterModal gameId={gameCenterGameId} onClose={() => setGameCenterGameId(null)} />
      <UserProfileModal userId={selectedUserId} onClose={() => setSelectedUserId(null)} />
    </div>
  )
}
